/**
 * Trunnion AI - Drag and Drop Hook
 * Custom HTML5 drag-and-drop hook for mission board workflow cards.
 * Manages drag state, data transfer serialization, and drop callbacks.
 */

import { useState, useCallback, type DragEvent } from 'react';

/** Represents an item being dragged across mission columns. */
export interface DragItem {
  /** Unique identifier for the dragged workflow */
  id: string;
  /** The type of draggable (e.g. 'workflow') */
  type: string;
  /** The mission column the item originated from */
  sourceColumn: string;
}

/** Configuration options for the drag-and-drop hook. */
interface UseDragAndDropOptions {
  /** Callback invoked when an item is dropped onto a target column */
  onDrop: (item: DragItem, targetColumn: string) => void;
}

/** Return value of the useDragAndDrop hook. */
interface UseDragAndDropReturn {
  /** The item currently being dragged, or null */
  draggedItem: DragItem | null;
  /** Whether a drag operation is in progress */
  isDragging: boolean;
  /** Handler to attach to draggable elements' onDragStart */
  handleDragStart: (e: DragEvent<HTMLElement>, item: DragItem) => void;
  /** Handler to attach to drop zone elements' onDragOver */
  handleDragOver: (e: DragEvent<HTMLElement>) => void;
  /** Handler to attach to drop zone elements' onDrop */
  handleDrop: (e: DragEvent<HTMLElement>, targetColumn: string) => void;
  /** Handler to attach to draggable elements' onDragEnd */
  handleDragEnd: () => void;
}

/**
 * Custom hook for HTML5 drag-and-drop interactions on the mission board.
 *
 * @param options - Configuration including the onDrop callback
 * @returns Drag state and event handlers to wire into drag sources and drop targets
 *
 * @example
 * ```tsx
 * const { draggedItem, isDragging, handleDragStart, handleDragOver, handleDrop, handleDragEnd } =
 *   useDragAndDrop({
 *     onDrop: (item, targetColumn) => moveWorkflow(item.id, item.sourceColumn, targetColumn),
 *   });
 * ```
 */
export function useDragAndDrop(options: UseDragAndDropOptions): UseDragAndDropReturn {
  const { onDrop } = options;

  const [draggedItem, setDraggedItem] = useState<DragItem | null>(null);

  /**
   * Initiates a drag operation. Serializes the DragItem into dataTransfer
   * so it can be read on drop, and sets the drag effect to "move".
   */
  const handleDragStart = useCallback(
    (e: DragEvent<HTMLElement>, item: DragItem) => {
      setDraggedItem(item);

      e.dataTransfer.setData('application/json', JSON.stringify(item));
      e.dataTransfer.effectAllowed = 'move';
    },
    [],
  );

  /**
   * Allows a drop on the target element by preventing the default
   * dragOver behavior and setting the drop effect to "move".
   */
  const handleDragOver = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  /**
   * Handles the drop event. Parses the serialized DragItem from dataTransfer
   * and invokes the onDrop callback only if the target column differs from
   * the source column.
   */
  const handleDrop = useCallback(
    (e: DragEvent<HTMLElement>, targetColumn: string) => {
      e.preventDefault();

      const rawData = e.dataTransfer.getData('application/json');
      if (!rawData) return;

      try {
        const item: DragItem = JSON.parse(rawData);

        // Only invoke callback when moving to a different column
        if (item.sourceColumn !== targetColumn) {
          onDrop(item, targetColumn);
        }
      } catch {
        // Silently ignore malformed drag data
      }

      setDraggedItem(null);
    },
    [onDrop],
  );

  /**
   * Cleans up drag state when a drag operation ends (whether successful or cancelled).
   */
  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
  }, []);

  return {
    draggedItem,
    isDragging: draggedItem !== null,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  };
}
